import * as React from "react";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { ChangeOptions } from "react-redux-form";
import * as ReactTooltip from "react-tooltip";
import { bindActionCreators } from "redux";
import { InlineLoading } from "src/components/common";
import Tree, { ITree } from "src/components/common/Tree/Tree";
import { FormFilterInput, SelectFormInput } from "src/components/form";
import { ProjectItemTemplatesBreadcrumbs } from "src/helpers/breadcrumbHelper";
import {
    findLookup,
    generateOptionsFromLookup,
} from "src/helpers/lookupHelper";
import { SettingsProjectsSubMenu } from "src/helpers/subMenuHelper";
import useNavigate from "src/hooks/useNavigate";
import * as NavigationActions from "../../../actions/navigation";
import * as ProjectSettingsPageActions from "../../../actions/pages/projectSettingsPage";
import * as ProjectItemTemplateActions from "../../../actions/projectItemTemplates";
import { Link } from "../../../components/routing/index";
import {
    FontAwesomeIcon,
    FontAwesomeIcons,
} from "../../../constants/fontAwesomeIcons";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import * as AuthenticationHelper from "../../../helpers/authentication";
import { IFormFilter, ITmdState } from "../../../reducers/index";
import { ProjectLookupsLayout } from "../../index";

import "./ProjectItemTemplatesPage.scss";

interface ProjectItemTemplatesPageProps {
    projectId: number;
    projectItemsByCategory: Dtos.ProjectItemTemplateCategory[];
    formFilter: IFormFilter;
    lookups: Dtos.Lookup[];
    loadingProjectItemTemplates: boolean;
    loadProjectItemTemplatesSuccess: boolean;
    loadProjectItemTemplatesFailure: boolean;
    loadProjectItemTemplatesErrorCode: string;
    permissions?: Dtos.PersonnelTmdPermissions[];
    clearProjectItemTemplates: ProjectItemTemplateActions.IProjectItemTemplateClearActionCreator;
    loadProjectItemTemplates: ProjectItemTemplateActions.IProjectItemTemplateLoadActionCreator;
    filter: ProjectItemTemplateActions.IProjectItemTemplateFilterActionCreator;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
    setProject: ProjectSettingsPageActions.IProjectSettingsPageSetProjectActionCreator;
}

const ProjectItemTemplatesPage: FC<ProjectItemTemplatesPageProps> = ({
    projectId,
    projectItemsByCategory,
    formFilter,
    lookups,
    loadingProjectItemTemplates,
    loadProjectItemTemplatesSuccess,
    loadProjectItemTemplatesFailure,
    loadProjectItemTemplatesErrorCode,
    permissions,
    clearProjectItemTemplates,
    loadProjectItemTemplates,
    filter,
    setBreadcrumbs,
    setDrawerItems,
    setProject
}) => {
    //
    // -- States --
    const [treeData, setTreeData] = useState<ITree | null>(null);
    const [initalizedTreeData, setInitalizedTreeData] =
        useState<boolean>(false);
    const [toggleState, setToggleState] = useState<string[]>([]);
    const [creatingTree, setCreatingTree] = useState<boolean>(false);

    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- Variables --
    const showProjectItemTemplates =
        !loadingProjectItemTemplates &&
        loadProjectItemTemplatesSuccess &&
        !creatingTree;

    function selectOnChange(value: any, _options?: ChangeOptions): any {
        setProject(value);
        clearProjectItemTemplates();
        setInitalizedTreeData(false);
        return undefined;
    }


    // Load Project Items Templates
    useEffect(() => {
        if (!projectItemsByCategory) {
            setDrawerItems(SettingsProjectsSubMenu(permissions));
            setBreadcrumbs(ProjectItemTemplatesBreadcrumbs());

            setTreeData(null);

            loadProjectItemTemplates(projectId);
        }
    }, [
        projectId,
        projectItemsByCategory,
        permissions,
        setDrawerItems,
        setBreadcrumbs,
        SettingsProjectsSubMenu,
        ProjectItemTemplatesBreadcrumbs,
        loadProjectItemTemplates,
        setTreeData,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadProjectItemTemplatesFailure) {
            navigate(`/error/${loadProjectItemTemplatesErrorCode}`);
        }
    }, [
        loadProjectItemTemplatesFailure,
        loadProjectItemTemplatesErrorCode,
        navigate,
    ]);

    // Cleanup effect to clear the project item templates on component unmount
    useEffect(() => {
        return () => {
            setTreeData(null);
            clearProjectItemTemplates();
        };
    }, [clearProjectItemTemplates, setTreeData]);

    // Create tree
    useEffect(() => {
        if (projectItemsByCategory && !initalizedTreeData) {
            setCreatingTree(true);

            // If no project items return
            if (
                !projectItemsByCategory ||
                projectItemsByCategory?.length == 0
            ) {
                setCreatingTree(false);
                return;
            }

            const createChildNodes = (items) => {
                let children = [];

                children =
                    items
                        ?.sort((a, b) => a?.id - b?.id)
                        ?.map((y) => ({
                            id: `item-${y.id}`,
                            data: (
                                <div
                                    style={{
                                        display: "grid",
                                        gridTemplateColumns: "1fr 4fr 7fr",
                                    }}
                                >
                                    <div className="px-1">
                                        <FontAwesomeIcon
                                            icon={FontAwesomeIcons.Duotone.FILE}
                                        />
                                    </div>
                                    <div className="px-1">{y?.name}</div>
                                    <div className="d-inline-block px-1 text-truncate">
                                        <div
                                            className="text-truncate"
                                            data-tip={y?.description}
                                        >
                                            {y?.description}
                                            <ReactTooltip />
                                        </div>
                                    </div>
                                </div>
                            ),
                            icon: null,
                            collapsible: false,
                        })) ?? [];

                // Add table header
                if (children?.length > 0) {
                    children.unshift({
                        id: "tableheader",
                        data: (
                            <div
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr 4fr 7fr",
                                }}
                            >
                                <div className="px-1"></div>
                                <div className="px-1">
                                    <strong>Name</strong>
                                </div>
                                <div className="px-1">
                                    <strong>Description</strong>
                                </div>
                            </div>
                        ),
                        icon: null,
                        collapsible: false,
                    });
                }

                return children;
            };

            let treeNodes =
                projectItemsByCategory
                    ?.sort((a, b) => a?.rank - b?.rank)
                    ?.map((x) => ({
                        id: `category-${x.id}`,
                        data: (
                            <div className="d-flex align-items-center">
                                <FontAwesomeIcon
                                    icon={FontAwesomeIcons.Duotone.SHAPES}
                                />
                                <span
                                    className="pl-4 mb-0"
                                    style={{ width: "275px" }}
                                >
                                    {x?.value}
                                </span>
                                <div
                                    className="d-flex align-items-center justify-content-center"
                                    style={{ width: "50px" }}
                                >
                                    <span className="badge badge-pill badge-secondary ml-4 mb-0">
                                        {x?.projectItemTemplates?.length}
                                    </span>
                                </div>
                            </div>
                        ),
                        icon: null,
                        collapsible: true,
                        subTree: {
                            children: [
                                ...createChildNodes(x.projectItemTemplates),
                            ],
                        },
                    })) ?? [];

            // Initialize tree toggle
            // const expanded = treeNodes.map((z) => z.id);

            // setToggleState(expanded);
            setTreeData({ children: treeNodes });
            setInitalizedTreeData(true);
            setCreatingTree(false);
        }
    }, [
        projectItemsByCategory,
        initalizedTreeData,
        treeData,
        formFilter,
        setTreeData,
        setToggleState,
        setInitalizedTreeData,
        setCreatingTree,
    ]);

    const handleToggle = useCallback(
        (id: string, navigateFlag?: boolean) => {
            if (navigateFlag) {
                if (
                    id.includes("item") &&
                    AuthenticationHelper.hasPermission(
                        permissions,
                        Dtos.Permission.TrialItemView
                    )
                ) {
                    navigate(
                        `/settings/project/itemtemplates/${id.replace(
                            "item-",
                            ""
                        )}`
                    );
                }
                if (id.includes("category")) {
                    setToggleState((prev) =>
                        prev.includes(id)
                            ? prev.filter((nodeId) => nodeId !== id)
                            : [...prev, id]
                    );
                }
            } else {
                setToggleState((prev) =>
                    prev.includes(id)
                        ? prev.filter((nodeId) => nodeId !== id)
                        : [...prev, id]
                );
            }
        },
        [setToggleState, navigate, permissions]
    );

    const renderContent = useMemo(() => {
        var projectOptions = generateOptionsFromLookup(
            findLookup("ProjectItemTemplate", "TrialId", lookups),
            projectId
        );

        return (
            <div>
                <div className="row">
                    <div className={"col-auto mb-2"}>
                        <h1>Project Item Templates</h1>
                    </div>
                    <div
                        className="col d-flex justify-content-end align-items-stretch"
                        style={{ height: "38px" }}
                    >
                        {projectOptions && projectOptions.length > 0 && (
                            <div
                                className="px-2 m-0"
                                style={{
                                    width: "100%",
                                    maxWidth: "350px",
                                    display: "inline-table",
                                }}
                            >
                                <SelectFormInput
                                    loading={null}
                                    multi={false}
                                    options={projectOptions}
                                    clearable={true}
                                    backspaceRemoves={false}
                                    name={"projectId"}
                                    id={"projectId"}
                                    placeholder="Select a project..."
                                    onChange={selectOnChange}
                                    value={projectId}
                                />
                            </div>
                        )}
                        <FormFilterInput
                            formFilter={formFilter}
                            onFilter={(e) => filter(e)}
                        />
                        {AuthenticationHelper.hasPermission(
                            permissions,
                            Dtos.Permission.ProjectItemTemplatesCreate
                        ) && (
                            <Link
                                className={"btn btn-primary"}
                                url={"/settings/project/itemtemplates/create"}
                            >
                                Create
                            </Link>
                        )}
                    </div>
                </div>
                {!showProjectItemTemplates ? (
                    <InlineLoading absoluteCenter={true} size={50} />
                ) : null}
                {showProjectItemTemplates ? (
                    <>
                        {treeData && treeData?.children?.length > 0 ? (
                            <Tree
                                treeData={treeData}
                                onToggle={handleToggle}
                                toggleState={toggleState}
                            />
                        ) : (
                            <div className="card card-shadow p-3">
                                <div className="alert alert-info d-flex mb-0">
                                    <div className="mr-2">
                                        <FontAwesomeIcon
                                            icon={
                                                FontAwesomeIcons.Solid
                                                    .INFO_SQUARE
                                            }
                                            fixedWidth
                                        />
                                    </div>
                                    <span>
                                        There are currently no items to view.
                                    </span>
                                </div>
                            </div>
                        )}
                    </>
                ) : null}
            </div>
        );
    }, [
        lookups,
        permissions,
        showProjectItemTemplates,
        treeData,
        handleToggle,
        toggleState,
        projectId,
        filter,
    ]);

    return (
        <ProjectLookupsLayout
            loading={loadingProjectItemTemplates}
            permission={Dtos.Permission.ProjectItemTemplatesView}
        >
            {renderContent}
        </ProjectLookupsLayout>
    );
};

const mapStateToProps = (
    state: ITmdState
): Partial<ProjectItemTemplatesPageProps> => {
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        projectId: state.projectSettingsPage.projectId,
        projectItemsByCategory:
            state.projectItemTemplates.formFilteredDataGrouped,
        formFilter: state.projectItemTemplates.formFilter,
        lookups: state.projectItemTemplates.lookups,
        loadingProjectItemTemplates:
            state.projectItemTemplates.loadState &&
            state.projectItemTemplates.loadState.status == RequestState.Pending,
        loadProjectItemTemplatesSuccess:
            state.projectItemTemplates.loadState &&
            state.projectItemTemplates.loadState.status == RequestState.Success,
        loadProjectItemTemplatesFailure:
            state.projectItemTemplates.loadState &&
            state.projectItemTemplates.loadState.status == RequestState.Failure,
        loadProjectItemTemplatesErrorCode:
            state.projectItemTemplates.loadState.errorCode,
        permissions: permissions,
    };
};

const mapDispatchToProps = (
    dispatch
): Partial<ProjectItemTemplatesPageProps> => {
    return {
        loadProjectItemTemplates: bindActionCreators(
            ProjectItemTemplateActions.LoadProjectItemTemplate,
            dispatch
        ),
        clearProjectItemTemplates: bindActionCreators(
            ProjectItemTemplateActions.Clear,
            dispatch
        ),
        filter: bindActionCreators(ProjectItemTemplateActions.Filter, dispatch),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        ),
        setProject: bindActionCreators(
            ProjectSettingsPageActions.SetProject,
            dispatch
        )
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(ProjectItemTemplatesPage);
